// src/pages/FabricColorManager.jsx
import React, { useState, useEffect } from "react";
import {
  Button,
  Spin,
  Modal,
  Form,
  Input,
  InputNumber,
  Upload,
  message,
  Image,
  Tooltip,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const FabricColorManager = () => {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFabric, setActiveFabric] = useState(null);
  const [fabricModalVisible, setFabricModalVisible] = useState(false);
  const [fabricModalForm] = Form.useForm();
  const [colorModalVisible, setColorModalVisible] = useState(false);
  const [colorModalForm] = Form.useForm();
  const [colorImagePreview, setColorImagePreview] = useState(null);
  const [colorSelectedFile, setColorSelectedFile] = useState(null);

  const userRole = String(localStorage.getItem("userRole") || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const isAdmin = userRole === "admin";
  const isSuperAdmin = userRole === "super admin";

  // ✅ Only super admin can see/edit fabric cost
  const canSeeFabricCost = isSuperAdmin;

  useEffect(() => {
    fetchFabrics();
  }, []);

  const fetchFabrics = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/fabrics");
      if (res.data && res.data.fabrics) {
        const fabricsWithColors = [];
        for (const fabric of res.data.fabrics) {
          try {
            const colorRes = await axiosInstance.get(
              `/admin/fabricColors/${fabric.id}`
            );
            fabric.colors = colorRes?.data?.fabricColors || [];
          } catch (error) {
            console.error("Error fetching colors for fabric:", fabric.id);
            fabric.colors = [];
          }

          // ensure numbers present
          fabric.fabric_price =
            typeof fabric.fabric_price === "number" ? fabric.fabric_price : 0;
          fabric.fabric_cost =
            typeof fabric.fabric_cost === "number" ? fabric.fabric_cost : 0;

          fabricsWithColors.push(fabric);
        }
        setFabrics(fabricsWithColors);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load fabrics");
    } finally {
      setLoading(false);
    }
  };

  const openAddFabricModal = () => {
    setActiveFabric(null);
    setFabricModalVisible(true);
    fabricModalForm.resetFields();
  };

  // ✅ FIX: do NOT set fabric_cost for admin (only for super admin)
  const openEditFabricModal = (fabric) => {
    setActiveFabric(fabric);

    const values = {
      fabric_name: fabric.fabric_name,
      fabric_type: fabric.fabric_type,
      fabric_price: Number(fabric.fabric_price || 0),
    };

    if (canSeeFabricCost) {
      values.fabric_cost = Number(fabric.fabric_cost || 0);
    }

    fabricModalForm.setFieldsValue(values);
    setFabricModalVisible(true);
  };

  // ✅ FIX: do NOT send fabric_cost in payload for admin
  const handleSaveFabric = async (values) => {
    try {
      const payload = {
        fabric_name: values.fabric_name,
        fabric_type: values.fabric_type,
        fabric_price: Number(values.fabric_price || 0),
      };

      // only super admin can send cost
      if (canSeeFabricCost) {
        payload.fabric_cost = Number(values.fabric_cost || 0);
      }

      let response = null;
      if (activeFabric) {
        response = await axiosInstance.put(
          `/admin/fabric/${activeFabric.id}`,
          payload
        );
      } else {
        response = await axiosInstance.post("/admin/fabric", payload);
      }

      if (response.data.success) {
        message.success("Fabric saved successfully!");
        setFabricModalVisible(false);
        fabricModalForm.resetFields();
        setActiveFabric(null);
        fetchFabrics();
      } else {
        message.error("Error saving fabric.");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to save fabric.");
    }
  };

  const handleDeleteFabric = async (fabricId) => {
    Modal.confirm({
      title: "Delete Fabric?",
      content: "Are you sure you want to delete this fabric?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await axiosInstance.delete(`/admin/fabric/${fabricId}`);
          message.success("Fabric deleted successfully!");
          fetchFabrics();
        } catch (error) {
          console.error(error);
          message.error("Failed to delete fabric.");
        }
      },
    });
  };

  const handleDeleteColor = (colorId) => {
    Modal.confirm({
      title: "Delete Color?",
      content: "Are you sure you want to delete this color?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await axiosInstance.delete(`/admin/fabric/fabricColor/${colorId}`);
          message.success("Color deleted successfully!");

          if (typeof fetchFabrics === "function") {
            await fetchFabrics();
            return;
          }

          setFabrics((prev) =>
            prev.map((f) => ({
              ...f,
              colors: (f.colors || []).filter((c) => c.id !== colorId),
            }))
          );
        } catch (error) {
          console.error(error);
          message.error("Failed to delete color.");
        }
      },
    });
  };

  const openAddColorModal = (fabric) => {
    setActiveFabric(fabric);
    colorModalForm.resetFields();
    setColorModalVisible(true);
    setColorImagePreview(null);
    setColorSelectedFile(null);
  };

  const handleColorImageChange = ({ file }) => {
    const selected = file.originFileObj || file;
    if (selected) {
      if (!selected.type.startsWith("image/")) {
        message.error("You can only upload image files!");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setColorImagePreview(e.target.result);
        setColorSelectedFile(selected);
      };
      reader.readAsDataURL(selected);
    }
  };

  const handleSaveColor = async (values) => {
    if (!activeFabric) return;
    if (!colorSelectedFile) {
      message.error("Please upload a color image!");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("fabricId", activeFabric.id);
      formData.append("color_name", values.color_name);
      formData.append("image", colorSelectedFile);
      const res = await axiosInstance.post("/admin/fabric_color", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        message.success("Color added successfully!");
        setColorModalVisible(false);
        colorModalForm.resetFields();
        setColorImagePreview(null);
        setColorSelectedFile(null);
        setActiveFabric(null);
        fetchFabrics();
      } else {
        message.error("Failed to add color.");
      }
    } catch (error) {
      console.error(error);
      message.error("Error adding color.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spin tip="Loading fabrics & colors..." size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Manage Fabrics & Colors
              </h1>
              <p className="text-gray-600">
                Organize fabric types, set fabric price, and manage their color
                variations
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAddFabricModal}
              className="bg-blue-600 hover:bg-blue-700 border-blue-600 h-10 px-4"
            >
              Add New Fabric
            </Button>
          </div>
        </div>

        {/* Fabrics List */}
        <div className="space-y-4">
          {fabrics.map((fabric, index) => (
            <div
              key={fabric.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Fabric Card */}
              <div className="flex items-stretch">
                {/* Serial Number with Vertical Line */}
                <div className="w-16 bg-gray-50 flex flex-col items-center justify-start py-4 relative">
                  <div className="text-lg font-bold text-gray-700">
                    {index + 1}
                  </div>
                  <div className="absolute top-0 bottom-0 right-0 w-px bg-gray-200"></div>
                </div>

                {/* Fabric Content */}
                <div className="flex-1 p-6">
                  {/* Fabric Header */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                    <div className="mb-3 lg:mb-0">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {fabric.fabric_name}
                      </h3>

                      <p className="text-sm text-gray-600">
                        Type: {fabric.fabric_type || "N/A"} • Fabric Price: €
                        {Number(fabric.fabric_price || 0).toFixed(2)}
                        {canSeeFabricCost && (
                          <> • Fabric Cost: £{Number(fabric.fabric_cost || 0).toFixed(2)}</>
                        )}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Tooltip title="Edit Fabric">
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => openEditFabricModal(fabric)}
                          className="border-blue-500 text-blue-500 hover:bg-blue-50"
                        >
                          Edit
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete Fabric">
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteFabric(fabric.id)}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Colors Section */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                      <h4 className="text-md font-semibold text-gray-800 mb-2 sm:mb-0">
                        Colors
                      </h4>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => openAddColorModal(fabric)}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        Add Color
                      </Button>
                    </div>

                    {/* Fixed-size color tiles */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {fabric.colors.map((color) => (
                        <div key={color.id} className="relative group">
                          <div className="color-card relative">
                            <Tooltip title="Delete Color">
                              <Button
                                shape="circle"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                className="absolute top-2 right-2 bg-white hover:bg-red-50 border border-red-200 z-10"
                                onClick={() => handleDeleteColor(color.id)}
                              />
                            </Tooltip>

                            <div className="color-media">
                              <Image
                                src={color.image_url}
                                alt={color.color_name}
                                preview={{ src: color.image_url }}
                                className="color-img cursor-zoom-in"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            </div>

                            <div className="mt-2 text-center">
                              <span className="text-sm font-medium text-gray-700 truncate block">
                                {color.color_name}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {fabric.colors.length === 0 && (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500">No colors added yet</p>
                        <p className="text-sm text-gray-400 mt-1">
                          Click "Add Color" to get started
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {fabrics.length === 0 && !loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="text-gray-400 mb-4">
              <PlusOutlined className="text-4xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No fabrics found
            </h3>
            <p className="text-gray-600 mb-4">
              Get started by adding your first fabric
            </p>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAddFabricModal}
              className="bg-blue-600 hover:bg-blue-700 border-blue-600"
            >
              Add First Fabric
            </Button>
          </div>
        )}
      </div>

      {/* Fabric Modal (Add/Edit) */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <PlusOutlined className="text-blue-500" />
            <span>{activeFabric ? "Edit Fabric" : "Add New Fabric"}</span>
          </div>
        }
        open={fabricModalVisible}
        onCancel={() => {
          setFabricModalVisible(false);
          setActiveFabric(null);
          fabricModalForm.resetFields();
        }}
        footer={null}
        className="modern-modal"
      >
        <Form
          form={fabricModalForm}
          layout="vertical"
          onFinish={handleSaveFabric}
        >
          <Form.Item
            name="fabric_name"
            label={
              <span className="text-sm font-medium text-gray-700">
                Fabric Name
              </span>
            }
            rules={[{ required: true, message: "Please enter the fabric name!" }]}
          >
            <Input
              placeholder="Enter fabric name"
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>

          <Form.Item
            name="fabric_type"
            label={
              <span className="text-sm font-medium text-gray-700">
                Fabric Type
              </span>
            }
            rules={[{ required: true, message: "Please enter the fabric type!" }]}
          >
            <Input
              placeholder="Enter fabric type"
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>

          {/* Fabric Price — Euro (€) */}
          <Form.Item
            name="fabric_price"
            label={
              <span className="text-sm font-medium text-gray-700">
                Fabric Price (€ Euro){" "}
                <span className="text-gray-400 text-xs">
                  (adds to final sale price)
                </span>
              </span>
            }
            tooltip="Leave 0 to not add any price"
            initialValue={0}
            rules={[{ type: "number", min: 0, message: "Price cannot be negative" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1}
              placeholder="€ (Euro)"
              addonBefore="€"
            />
          </Form.Item>

          {/* ✅ Fabric Cost ONLY for super admin */}
          {canSeeFabricCost && (
            <Form.Item
              name="fabric_cost"
              label={
                <span className="text-sm font-medium text-gray-700">
                  Fabric Cost (£ GBP){" "}
                  <span className="text-gray-400 text-xs">
                    (adds to factory cost)
                  </span>
                </span>
              }
              tooltip="Used for production inventory costing. Leave 0 if not applicable."
              initialValue={0}
              rules={[{ type: "number", min: 0, message: "Cost cannot be negative" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1}
                placeholder="£ (GBP)"
                addonBefore="£"
              />
            </Form.Item>
          )}

          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setFabricModalVisible(false);
                  setActiveFabric(null);
                  fabricModalForm.resetFields();
                }}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                {activeFabric ? "Update Fabric" : "Add Fabric"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Color Modal (Add Color) */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <PlusOutlined className="text-blue-500" />
            <span>
              Add Color for "{activeFabric?.fabric_name || "Selected Fabric"}"
            </span>
          </div>
        }
        open={colorModalVisible}
        onCancel={() => {
          setColorModalVisible(false);
          colorModalForm.resetFields();
          setColorImagePreview(null);
          setColorSelectedFile(null);
          setActiveFabric(null);
        }}
        footer={null}
        className="modern-modal"
      >
        <Form form={colorModalForm} layout="vertical" onFinish={handleSaveColor}>
          <Form.Item
            name="image"
            label={
              <span className="text-sm font-medium text-gray-700">
                Color Image
              </span>
            }
            rules={[{ required: true, message: "Please upload a color image!" }]}
          >
            <Upload
              beforeUpload={() => false}
              listType="picture-card"
              showUploadList={false}
              onChange={handleColorImageChange}
              className="color-upload"
            >
              {colorImagePreview ? (
                <Image
                  src={colorImagePreview}
                  alt="Preview"
                  preview={false}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  className="rounded-lg"
                />
              ) : (
                <div className="text-center">
                  <PlusOutlined className="text-2xl text-gray-400 mb-2" />
                  <div className="text-sm text-gray-500">Click to Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="color_name"
            label={
              <span className="text-sm font-medium text-gray-700">
                Color Name
              </span>
            }
            rules={[{ required: true, message: "Please enter the color name!" }]}
          >
            <Input
              placeholder="Enter color name"
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setColorModalVisible(false);
                  colorModalForm.resetFields();
                  setColorImagePreview(null);
                  setColorSelectedFile(null);
                  setActiveFabric(null);
                }}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Add Color
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .ant-input {
          border-radius: 8px !important;
        }

        /* === CONSISTENT TILE SIZE FOR ALL COLOR CARDS === */
        .color-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 12px;
          transition: box-shadow 0.15s ease;
        }
        .color-card:hover {
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
        }

        .color-media {
          width: 100%;
          height: 120px;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
          background: #f3f4f6;
        }

        .color-media .ant-image,
        .color-media .ant-image-img,
        .color-media img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          display: block !important;
        }

        .color-upload .ant-upload {
          width: 100% !important;
          height: 160px !important;
          border-radius: 8px !important;
        }
      `}</style>
    </div>
  );
};

export default FabricColorManager;
